import * as React from 'react'
import {
  motion,
  AnimatePresence,
  useMotionValue,
  useTransform,
} from 'motion/react'
import classNames from 'classnames'
import { flushSync } from 'react-dom'
// constants
import {
  SpotlightData,
  SWIPE_THRESHOLD,
} from '../../../../../constants/spotlight'
// styles
import './styles.css'
// assets
import { ReactComponent as Arrow } from '../../../../../assets/imgs/arrow.svg'

const SpotlightCarousel = () => {
  const [activeIdx, setActiveIdx] = React.useState(0)
  const [direction, setDirection] = React.useState(0)
  const clipRef = React.useRef(null)
  const isFirstRender = React.useRef(true)

  const x = useMotionValue(0)
  const opacity = useTransform(
    x,
    [-SWIPE_THRESHOLD * 2, 0, SWIPE_THRESHOLD * 2],
    [0.1, 1, 0.1]
  )
  const scale = useTransform(
    x,
    [-SWIPE_THRESHOLD * 2, 0, SWIPE_THRESHOLD * 2],
    [0.95, 1, 0.95]
  )

  React.useEffect(() => {
    const timer = setTimeout(() => {
      isFirstRender.current = false
    }, 1000)

    const interval = setInterval(() => {
      const nextIdx = (activeIdx + 1) % SpotlightData.length
      setActiveIdx(nextIdx)
    }, 7000)

    return () => {
      clearInterval(interval)
      clearTimeout(timer)
    }
  }, [activeIdx])

  const currentItem = SpotlightData[activeIdx]

  const next = () => {
    flushSync(() => setDirection(1))
    setActiveIdx((prev) => (prev + 1) % SpotlightData.length)
  }
  const previous = () => {
    flushSync(() => setDirection(-1))
    setActiveIdx(
      (prev) => (prev - 1 + SpotlightData.length) % SpotlightData.length
    )
  }

  const onDragEnd = (_, info) => {
    const dx = info.offset.x

    if (dx <= -SWIPE_THRESHOLD) {
      next()
    } else if (dx >= SWIPE_THRESHOLD) {
      previous()
    }
  }

  return (
    <>
      <AnimatePresence mode='wait'>
        <div className='spotlight-card' ref={clipRef}>
          <div className='desktop-arrow desktop-arrow-left' onClick={previous}>
            <Arrow />
          </div>
          <motion.div
            key={`spotlight-${activeIdx}`}
            className='spotlight-card-inner'
            initial={{ opacity: 0, x: direction > 0 ? 30 : -30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: direction > 0 ? -30 : 30 }}
            transition={{ duration: 0.4 }}
            // swipe
            drag='x'
            dragConstraints={clipRef}
            dragElastic={0.2}
            dragMomentum={false}
            onDragEnd={onDragEnd}
            whileDrag={{ cursor: 'grabbing' }}
            style={{ x, scale, opacity }}
          >
            <motion.p
              className='spotlight-card-title'
              initial={isFirstRender.current ? { opacity: 0 } : { opacity: 1 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{
                duration: isFirstRender.current ? 1 : 0,
              }}
            >
              {currentItem.title}
            </motion.p>
            <motion.p
              className='spotlight-card-description'
              initial={isFirstRender.current ? { opacity: 0 } : { opacity: 1 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{
                duration: isFirstRender.current ? 1 : 0,
              }}
            >
              {currentItem.description}
            </motion.p>
            <motion.div className='spotlight-card-image' draggable={false}>
              <motion.img
                key={`spotlight-img-${activeIdx}`}
                src={currentItem.src}
                alt={currentItem.alt}
                initial={
                  isFirstRender.current ? { opacity: 0 } : { opacity: 1 }
                }
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, amount: 0.5 }}
                transition={{
                  duration: isFirstRender.current ? 1 : 0,
                }}
                draggable={false}
              />
            </motion.div>
          </motion.div>
          <div className='desktop-arrow' onClick={next}>
            <Arrow />
          </div>
        </div>
      </AnimatePresence>
      <div className='dots-container'>
        {SpotlightData.map((_, idx) => (
          <span
            key={`spotlight-dot-${idx}`}
            className={classNames('dot', { active: idx === activeIdx })}
            onClick={() => setActiveIdx(idx)}
          />
        ))}
      </div>
    </>
  )
}

export default SpotlightCarousel
